// =============================================================================
// SPEECH SERVICE - Speech Recognition & Text-to-Speech for Sparring
// =============================================================================
//
// Speech Recognition: Web Speech API (browser-native, free)
// Text-to-Speech: Eleven Labs (high quality) with Web Speech API fallback
//
// =============================================================================

// -----------------------------------------------------------------------------
// WEB SPEECH API TYPE DEFINITIONS
// -----------------------------------------------------------------------------

interface ISpeechRecognitionAlternative {
  readonly confidence: number;
  readonly transcript: string;
}

interface ISpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): ISpeechRecognitionAlternative;
  [index: number]: ISpeechRecognitionAlternative;
}

interface ISpeechRecognitionResultList {
  readonly length: number;
  item(index: number): ISpeechRecognitionResult;
  [index: number]: ISpeechRecognitionResult;
}

interface ISpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: ISpeechRecognitionResultList;
}

interface ISpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface ISpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onaudiostart: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onend: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onerror: ((this: ISpeechRecognition, ev: ISpeechRecognitionErrorEvent) => void) | null;
  onnomatch: ((this: ISpeechRecognition, ev: ISpeechRecognitionEvent) => void) | null;
  onresult: ((this: ISpeechRecognition, ev: ISpeechRecognitionEvent) => void) | null;
  onsoundend: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onsoundstart: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onspeechend: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onspeechstart: ((this: ISpeechRecognition, ev: Event) => void) | null;
  onstart: ((this: ISpeechRecognition, ev: Event) => void) | null;
  abort(): void;
  start(): void;
  stop(): void;
}

interface ISpeechRecognitionConstructor {
  new (): ISpeechRecognition;
}

// Extend Window interface for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: ISpeechRecognitionConstructor;
    webkitSpeechRecognition: ISpeechRecognitionConstructor;
  }
}

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export interface SpeechRecognitionResult {
  transcript: string;
  isFinal: boolean;
}

export interface SpeechServiceConfig {
  language?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

// -----------------------------------------------------------------------------
// SPEECH RECOGNITION (Web Speech API)
// -----------------------------------------------------------------------------

class SpeechRecognitionService {
  private recognition: ISpeechRecognition | null = null;
  private isListening: boolean = false;

  /**
   * Check if speech recognition is supported
   */
  isSupported(): boolean {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  /**
   * Start listening for speech
   */
  startListening(
    onResult: (result: SpeechRecognitionResult) => void,
    onError: (error: string) => void,
    onEnd: () => void,
    config: SpeechServiceConfig = {}
  ): boolean {
    if (!this.isSupported()) {
      onError('Spraakherkenning wordt niet ondersteund in deze browser. Probeer Chrome of Edge.');
      return false;
    }

    if (this.isListening) {
      this.stopListening();
    }

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognitionClass();

    // Configure
    this.recognition.lang = config.language || 'nl-NL';
    this.recognition.continuous = config.continuous ?? false;
    this.recognition.interimResults = config.interimResults ?? true;

    // Event handlers
    this.recognition.onstart = () => {
      this.isListening = true;
    };

    this.recognition.onresult = (event: ISpeechRecognitionEvent) => {
      const result = event.results[event.results.length - 1];
      onResult({
        transcript: result[0].transcript,
        isFinal: result.isFinal
      });
    };

    this.recognition.onerror = (event: ISpeechRecognitionErrorEvent) => {
      // IGNORE aborted - this happens during normal stop/restart and should not trigger error callback
      if (event.error === 'aborted') {
        this.isListening = false;
        return; // Don't call onError, don't restart
      }
      
      let errorMessage = 'Spraakherkenning fout';
      
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'Geen spraak gedetecteerd. Probeer opnieuw.';
          break;
        case 'audio-capture':
          errorMessage = 'Geen microfoon gevonden. Controleer je microfoon.';
          break;
        case 'not-allowed':
          errorMessage = 'Microfoon toegang geweigerd. Sta microfoon toegang toe in je browser.';
          break;
        case 'network':
          errorMessage = 'Netwerkfout bij spraakherkenning.';
          break;
        default:
          errorMessage = `Spraakherkenning fout: ${event.error}`;
      }
      
      onError(errorMessage);
      this.isListening = false;
    };

    this.recognition.onend = () => {
      this.isListening = false;
      onEnd();
    };

    try {
      this.recognition.start();
      return true;
    } catch (error) {
      onError('Kon spraakherkenning niet starten.');
      return false;
    }
  }

  /**
   * Stop listening
   */
  stopListening(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    }
  }

  /**
   * Check if currently listening
   */
  getIsListening(): boolean {
    return this.isListening;
  }
}

// -----------------------------------------------------------------------------
// TEXT-TO-SPEECH (Eleven Labs + Web Speech API fallback)
// -----------------------------------------------------------------------------

class TextToSpeechService {
  private elevenLabsKey: string | null = null;
  private voiceId: string = '21m00Tcm4TlvDq8ikWAM'; // Rachel - warm, vrouwelijk
  private currentAudio: HTMLAudioElement | null = null;
  private isSpeaking: boolean = false;

  constructor() {
    this.elevenLabsKey = process.env.REACT_APP_ELEVENLABS_API_KEY || null;
    this.voiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID || '21m00Tcm4TlvDq8ikWAM';
  }

  /**
   * Check if TTS is available
   */
  isAvailable(): boolean {
    return !!(this.elevenLabsKey || window.speechSynthesis);
  }

  /**
   * Get which TTS provider is being used
   */
  getProvider(): 'elevenlabs' | 'browser' | 'none' {
    if (this.elevenLabsKey) return 'elevenlabs';
    if (window.speechSynthesis) return 'browser';
    return 'none';
  }

  /**
   * Speak text using TTS
   */
  async speak(
    text: string,
    onStart?: () => void,
    onEnd?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    // Stop any current speech
    this.stop();

    if (this.elevenLabsKey) {
      return this.speakWithElevenLabs(text, onStart, onEnd, onError);
    } else if (window.speechSynthesis) {
      return this.speakWithBrowser(text, onStart, onEnd, onError);
    } else {
      onError?.('Text-to-speech is niet beschikbaar.');
    }
  }

  /**
   * Speak using Eleven Labs API
   */
  private async speakWithElevenLabs(
    text: string,
    onStart?: () => void,
    onEnd?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    try {
      onStart?.();
      this.isSpeaking = true;

      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${this.voiceId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xi-api-key': this.elevenLabsKey!
          },
          body: JSON.stringify({
            text,
            model_id: 'eleven_multilingual_v2',
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.75
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Eleven Labs API error: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      this.currentAudio = new Audio(audioUrl);
      
      this.currentAudio.onended = () => {
        this.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        onEnd?.();
      };

      this.currentAudio.onerror = () => {
        this.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        onError?.('Fout bij afspelen audio.');
      };

      await this.currentAudio.play();
    } catch (error: any) {
      console.error('Eleven Labs TTS error:', error);
      this.isSpeaking = false;
      
      // Fallback to browser TTS
      if (window.speechSynthesis) {
        console.log('Falling back to browser TTS...');
        return this.speakWithBrowser(text, onStart, onEnd, onError);
      }
      
      onError?.(error.message || 'Fout bij text-to-speech.');
    }
  }

  /**
   * Speak using browser Web Speech API
   */
  private speakWithBrowser(
    text: string,
    onStart?: () => void,
    onEnd?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'nl-NL';
      utterance.rate = 0.9;
      utterance.pitch = 1.0;

      // Try to find a Dutch voice
      const voices = window.speechSynthesis.getVoices();
      const dutchVoice = voices.find(v => v.lang.startsWith('nl'));
      if (dutchVoice) {
        utterance.voice = dutchVoice;
      }

      utterance.onstart = () => {
        this.isSpeaking = true;
        onStart?.();
      };

      utterance.onend = () => {
        this.isSpeaking = false;
        onEnd?.();
        resolve();
      };

      utterance.onerror = (event) => {
        this.isSpeaking = false;
        onError?.(`Browser TTS fout: ${event.error}`);
        resolve();
      };

      window.speechSynthesis.speak(utterance);
    });
  }

  /**
   * Stop current speech
   */
  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    this.isSpeaking = false;
  }

  /**
   * Check if currently speaking
   */
  getIsSpeaking(): boolean {
    return this.isSpeaking;
  }
}

// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export const speechRecognitionService = new SpeechRecognitionService();
export const textToSpeechService = new TextToSpeechService();
